/**
 * ScoreFilterBar.tsx
 *
 * Score type filter bar for the Zandi submarket map.
 * Allows users to switch between different scoring dimensions.
 */

import { useState } from 'react';
import {
  LayoutGrid,
  TrendingUp,
  Home,
  Activity,
  Users,
  DollarSign,
  Hammer,
  ArrowRightLeft,
  ChevronDown,
  type LucideIcon,
} from 'lucide-react';
import { useSubmarketStore, type ScoreType } from '../../store/useSubmarketStore';
import { getScoreLabel } from '../../hooks/useSubmarketScores';

interface ScoreOption {
  id: ScoreType;
  label: string;
  icon: LucideIcon;
  weight?: string;
  description: string;
}

const SCORE_OPTIONS: ScoreOption[] = [
  {
    id: 'composite',
    label: 'Composite',
    icon: LayoutGrid,
    description: 'Weighted average of all 7 components',
  },
  {
    id: 'rent_gap',
    label: 'Rent Gap',
    icon: DollarSign,
    weight: '25%',
    description: 'EGI below metro = upside',
  },
  {
    id: 'mobi',
    label: 'MOBI',
    icon: Home,
    weight: '15%',
    description: 'Ownership barriers',
  },
  {
    id: 'momentum',
    label: 'Momentum',
    icon: TrendingUp,
    weight: '15%',
    description: 'Market cycle position',
  },
  {
    id: 'vacancy',
    label: 'Vacancy',
    icon: Users,
    weight: '15%',
    description: 'Demand indicator',
  },
  {
    id: 'valuation',
    label: 'Valuation',
    icon: Activity,
    weight: '10%',
    description: 'Price vs rent growth',
  },
  {
    id: 'permit',
    label: 'Permits',
    icon: Hammer,
    weight: '10%',
    description: 'Development activity',
  },
  {
    id: 'spillover',
    label: 'Spillover',
    icon: ArrowRightLeft,
    weight: '10%',
    description: 'Adjacent momentum',
  },
];

interface ScoreFilterBarProps {
  /** Layout orientation */
  orientation?: 'horizontal' | 'vertical';
  /** Compact mode - icons only on mobile */
  compact?: boolean;
}

/**
 * ScoreFilterBar - Horizontal score type filter bar.
 *
 * Allows users to switch between different scoring dimensions
 * to color the submarket choropleth map.
 */
export function ScoreFilterBar({
  orientation = 'horizontal',
  compact = false,
}: ScoreFilterBarProps) {
  const { selectedScoreType, setSelectedScoreType } = useSubmarketStore();
  const [hoveredOption, setHoveredOption] = useState<ScoreType | null>(null);

  const isHorizontal = orientation === 'horizontal';

  return (
    <div
      className={`
        relative bg-charcoal/95 backdrop-blur-sm rounded-lg border border-slate-dark p-1.5
        ${isHorizontal ? 'flex flex-wrap gap-1' : 'flex flex-col gap-1'}
      `}
    >
      {SCORE_OPTIONS.map((option) => {
        const Icon = option.icon;
        const isActive = selectedScoreType === option.id;
        const isHovered = hoveredOption === option.id;

        return (
          <div key={option.id} className="relative">
            <button
              onClick={() => setSelectedScoreType(option.id)}
              onMouseEnter={() => setHoveredOption(option.id)}
              onMouseLeave={() => setHoveredOption(null)}
              className={`
                flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium
                transition-all duration-150 ease-out
                ${
                  isActive
                    ? 'bg-gold text-[#1c1917] shadow-sm'
                    : 'text-stone-600 hover:text-ivory hover:bg-graphite'
                }
              `}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {!compact && (
                <>
                  <span className="hidden sm:inline">{option.label}</span>
                  {option.weight && (
                    <span
                      className={`
                        hidden md:inline text-xs ml-0.5
                        ${isActive ? 'text-stone-600' : 'text-stone-400'}
                      `}
                    >
                      ({option.weight})
                    </span>
                  )}
                </>
              )}
            </button>

            {/* Tooltip */}
            {isHovered && !isActive && (
              <div
                className={`
                  absolute z-50 px-2.5 py-1.5 bg-obsidian text-ivory text-xs rounded-md
                  shadow-lg border border-slate-dark whitespace-nowrap
                  ${isHorizontal ? 'left-1/2 -translate-x-1/2 top-full mt-2' : 'left-full ml-2 top-1/2 -translate-y-1/2'}
                `}
              >
                <div className="font-medium">{getScoreLabel(option.id)}</div>
                <div className="text-stone-500 mt-0.5">{option.description}</div>
                {/* Tooltip arrow */}
                <div
                  className={`
                    absolute w-2 h-2 bg-obsidian border border-slate-dark rotate-45
                    ${isHorizontal ? 'left-1/2 -translate-x-1/2 -top-1 border-b-0 border-r-0' : 'top-1/2 -translate-y-1/2 -left-1 border-t-0 border-r-0'}
                  `}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface ScoreToggleProps {
  /** Additional CSS classes */
  className?: string;
}

/**
 * ScoreToggle - Compact dropdown version for mobile/constrained spaces.
 *
 * Shows a single button with dropdown to select score type.
 */
export function ScoreToggle({ className = '' }: ScoreToggleProps) {
  const { selectedScoreType, setSelectedScoreType } = useSubmarketStore();
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = SCORE_OPTIONS.find((o) => o.id === selectedScoreType) || SCORE_OPTIONS[0];
  const SelectedIcon = selectedOption.icon;

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-charcoal/95 backdrop-blur-sm rounded-lg border border-slate-dark text-ivory hover:bg-graphite transition-colors"
      >
        <SelectedIcon className="w-4 h-4" />
        <span className="text-sm font-medium">{selectedOption.label}</span>
        <ChevronDown
          className={`w-4 h-4 text-stone-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <>
          {/* Backdrop to close on click outside */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          {/* Dropdown menu */}
          <div className="absolute top-full left-0 mt-1 z-50 bg-charcoal/95 backdrop-blur-sm rounded-lg border border-slate-dark shadow-xl overflow-hidden min-w-48">
            {SCORE_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isActive = selectedScoreType === option.id;

              return (
                <button
                  key={option.id}
                  onClick={() => {
                    setSelectedScoreType(option.id);
                    setIsOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 text-left
                    transition-colors
                    ${isActive ? 'bg-gold text-[#1c1917]' : 'text-ivory hover:bg-graphite'}
                  `}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium flex items-center gap-2">
                      {option.label}
                      {option.weight && (
                        <span
                          className={`text-xs ${isActive ? 'text-stone-600' : 'text-stone-400'}`}
                        >
                          {option.weight}
                        </span>
                      )}
                    </div>
                    <div
                      className={`text-xs mt-0.5 ${isActive ? 'text-stone-700' : 'text-stone-500'}`}
                    >
                      {option.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default ScoreFilterBar;
